import { element } from './dom';

/** Called when the toggle is pressed. */
export type FullscreenToggleListener = () => void;

/** The two faces of the button, as glyph and the label a screen reader hears. */
const FACES = {
  enter: { glyph: '⛶', label: 'Fill the screen' },
  leave: { glyph: '✕', label: 'Leave fullscreen' },
} as const;

/**
 * The picker's second control: a switch into and out of fullscreen.
 *
 * It lives on the picker and nowhere else. Fullscreen is something a grown-up sets up before handing
 * the tablet over, not something the game needs, and the bar over a running game stays at one control
 * for the reason written on it. Fullscreen survives the move between the two, so setting it once holds
 * for every game afterwards.
 *
 * Both faces are the same button rather than an enter button that hides itself, because this is the only
 * way out the site ever offers. A tablet has no escape key, and stranding a grown-up in a screen they
 * cannot leave is worse than a pixel a toddler might press.
 */
export class FullscreenToggle {
  /** Root element, appended by the shell. */
  readonly element: HTMLButtonElement;

  constructor(onToggle: FullscreenToggleListener) {
    this.element = element('button', 'portal-fullscreen');
    this.element.type = 'button';
    this.element.addEventListener('click', onToggle);
    this.setActive(false);
  }

  /** Whether the button is on screen. */
  get visible(): boolean {
    return !this.element.hidden;
  }

  set visible(value: boolean) {
    this.element.hidden = !value;
  }

  /** Shows the way out or the way in. Driven by the engine, so a key or a gesture is reflected too. */
  setActive(isActive: boolean): void {
    const face = isActive ? FACES.leave : FACES.enter;
    this.element.textContent = face.glyph;
    this.element.setAttribute('aria-label', face.label);
  }

  /** Removes the button from the page. */
  dispose(): void {
    this.element.remove();
  }
}
